import { useState, useEffect, useRef, useCallback } from "react";

interface Bubble {
  id: number;
  x: number;
  startY: number;
  color: "teal" | "orange";
  born: number;
  swayOffset: number;
}

const LIFETIME = 2000;
const RISE_PX = 300;
const SWAY_AMP = 25;
const BUBBLE_RADIUS = 5;

function getClickableRects(): DOMRect[] {
  const els = document.querySelectorAll("button, a, [role='button'], input, select, textarea");
  const rects: DOMRect[] = [];
  for (const el of els) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) rects.push(r);
  }
  return rects;
}

function isNearClickable(x: number, pathTopY: number, pathBottomY: number, rects: DOMRect[], margin = 30): boolean {
  for (const r of rects) {
    const inX = x > r.left - margin && x < r.right + margin;
    const pathOverlaps = pathTopY < r.bottom + margin && pathBottomY > r.top - margin;
    if (inX && pathOverlaps) return true;
  }
  return false;
}

export default function ClickBubbles() {
  const bubblesRef = useRef<Bubble[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [popup, setPopup] = useState<{ x: number; y: number } | null>(null);
  const idRef = useRef(0);
  /* Starts the draw loop when it is idle. Set by the canvas effect below. */
  const wakeRef = useRef<() => void>(() => {});

  const spawn = useCallback(() => {
    /* A background tab shows nothing, so it measures nothing either. */
    if (document.visibilityState !== "visible") return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rects = getClickableRects();

    const startY = vh * (0.4 + Math.random() * 0.55);
    const endY = startY - RISE_PX;

    let x = 0;
    let attempts = 0;
    do {
      x = Math.random() * (vw - 60) + 30;
      attempts++;
    } while (isNearClickable(x, endY, startY, rects) && attempts < 15);

    if (attempts >= 15) return;

    bubblesRef.current.push({
      id: idRef.current++,
      x,
      startY,
      color: Math.random() > 0.5 ? "teal" : "orange",
      born: performance.now(),
      swayOffset: Math.random() * Math.PI * 2,
    });
    wakeRef.current();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      spawn();
    }, 5000);
    return () => clearInterval(interval);
  }, [spawn]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let raf = 0;
    let running = false;
    const ctx = canvas.getContext("2d")!;

    /* Sized when the window is, not every frame. Assigning canvas.width
       reallocates and wipes the backing store, and this did it sixty times a
       second for as long as the homepage was open. */
    const size = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    size();
    window.addEventListener("resize", size);

    /* A bubble appears every five seconds and lives for two, so the canvas is
       empty most of the time. With nothing on it, there is nothing to draw. */
    let drawn = false;

    const draw = (now: number) => {
      bubblesRef.current = bubblesRef.current.filter((b) => now - b.born < LIFETIME);
      /* Nothing left to draw: clear once and stop. The loop used to keep
         running every frame on an empty canvas; spawn() wakes it again. */
      if (bubblesRef.current.length === 0) {
        if (drawn) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          drawn = false;
        }
        running = false;
        return;
      }
      drawn = true;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const b of bubblesRef.current) {
        const age = now - b.born;
        const t = age / LIFETIME;
        const y = b.startY - t * RISE_PX;
        const x = b.x + Math.sin(age / 300 + b.swayOffset) * SWAY_AMP;
        const opacity = t < 0.75 ? 1 : 1 - (t - 0.75) / 0.25;

        const isTeal = b.color === "teal";
        const r = isTeal ? 45 : 249;
        const g = isTeal ? 212 : 115;
        const bl = isTeal ? 191 : 22;

        /* The soft glow as a radial gradient: the same look as the blur(5px)
           filter it replaces, which re-blurred every bubble on every frame. */
        ctx.save();
        ctx.globalAlpha = opacity * 0.35;
        const glow = ctx.createRadialGradient(x, y, 0, x, y, 19);
        glow.addColorStop(0, `rgba(${r},${g},${bl},1)`);
        glow.addColorStop(0.55, `rgba(${r},${g},${bl},0.6)`);
        glow.addColorStop(1, `rgba(${r},${g},${bl},0)`);
        ctx.beginPath();
        ctx.arc(x, y, 19, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();
        ctx.restore();

        ctx.save();
        ctx.globalAlpha = opacity;
        ctx.beginPath();
        ctx.arc(x, y, BUBBLE_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = `rgb(${r},${g},${bl})`;
        ctx.fill();
        ctx.restore();
      }

      raf = requestAnimationFrame(draw);
    };

    wakeRef.current = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(draw);
    };
    wakeRef.current();
    return () => {
      cancelAnimationFrame(raf);
      running = false;
      wakeRef.current = () => {};
      window.removeEventListener("resize", size);
    };
  }, []);

  useEffect(() => {
    if (!popup) return;
    const t = setTimeout(() => setPopup(null), 2000);
    return () => clearTimeout(t);
  }, [popup]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const now = performance.now();
      const mx = e.clientX;
      const my = e.clientY;

      for (let i = bubblesRef.current.length - 1; i >= 0; i--) {
        const b = bubblesRef.current[i];
        const age = now - b.born;
        const t = age / LIFETIME;
        const y = b.startY - t * RISE_PX;
        const x = b.x + Math.sin(age / 300 + b.swayOffset) * SWAY_AMP;
        const dist = Math.sqrt((mx - x) ** 2 + (my - y) ** 2);
        if (dist < 18) {
          bubblesRef.current.splice(i, 1);
          setPopup({ x: mx, y: my });
          return;
        }
      }
    };
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9998,
          pointerEvents: "none",
        }}
      />

      {popup && (
        <div
          style={{
            position: "fixed",
            left: popup.x,
            top: popup.y - 60,
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            background: "rgba(7,21,24,0.95)",
            border: "1px solid rgba(45,212,191,0.4)",
            borderRadius: 12,
            padding: "16px 24px",
            textAlign: "center",
            animation: "popIn 0.3s ease",
            backdropFilter: "blur(8px)",
          }}
        >
          <p style={{ color: "#2dd4bf", fontWeight: 700, fontSize: 16, margin: 0 }}>
            Congratulations
          </p>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 13, marginTop: 4 }}>
            SUPRISE10
          </p>
        </div>
      )}

      <style>{`
        @keyframes popIn {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
      `}</style>
    </>
  );
}
